/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Vu Dinh Luan (vdluan@rsupartners.com)
 *
 * @description
 *
 * @created at Mon Dec 18 2024
 **/
import { useTheme } from "@emotion/react";
import dayjs from "dayjs";

import { Typography, Row, Column, Icon } from "@/frameworks/designStuff/components";
import { _T } from "@rfice/language";
import { ChronoUtils, SwitchCase } from "@/frameworks/libraries";

import { S } from "../..";

const getTime = (data?: { startTime: number; endTime: number }) => {
	const diff = dayjs(data?.endTime || 0).diff(dayjs(data?.startTime || 0));
	let seconds = diff / 1000;
	const hours = Math.floor(seconds / 3600);
	seconds %= 3600;

	// Calculate minutes
	const minutes = Math.floor(seconds / 60);
	seconds %= 60;

	return { hours, minutes, seconds };
};

interface VoiceSystemChatProps {
	message: string;
	variant: ChatUI.Size;
	createAt?: number;
	type: "start" | "end" | "join";
	data?: { startTime: number; endTime: number };
}

const VoiceSystemChat = ({ message, variant, createAt, type, data }: VoiceSystemChatProps) => {
	const { hours, minutes, seconds } = getTime(data);
	const theme = useTheme();

	return (
		<S.SystemChatRow
			css={{
				borderRadius: "12px",
				padding: "8px 16px",
				backgroundColor: theme.colors.neutral_00_68
			}}
		>
			<Column css={{ width: "100%" }}>
				<S.ChatContent variant={variant}>
					<Row gap={10} verticalAlign="center">
						<S.IconCallWrapper>
							<Icon name="VoiceChat24" size={16} fill={theme.colors.neutral_00_100} />
						</S.IconCallWrapper>
						<Typography typoSize="Title2">{message}</Typography>
					</Row>
					<Typography typoSize="Body4">{ChronoUtils.formatTimeAgo(createAt || 0)}</Typography>
				</S.ChatContent>
				<SwitchCase
					condition={type}
					cases={{
						start: null,
						join: (
							<Typography typoSize="Body2" style={{ paddingLeft: "40px" }}>
								{_T(
									"보이스챗 참여중에 전화를 받을 경우 보이스챗이 자동으로 종료되며, 보이스챗에 참여하는 동안 오디오존은 생성이 불가합니다."
								)}
							</Typography>
						),
						end: (
							<Typography typoSize="Body2" style={{ paddingLeft: "40px" }}>
								{_T("총 {{hours}}시간 {{minutes}}분 {{seconds}}초 동안 보이스챗이 진행되었습니다.", {
									hours: hours,
									minutes: minutes,
									seconds: Math.floor(seconds)
								})}
							</Typography>
						)
					}}
				/>
			</Column>
		</S.SystemChatRow>
	);
};

export default VoiceSystemChat;
